/* import __COLOCATED_TEMPLATE__ from './users-list.hbs'; */
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { USER } from '../models/user';
import { EMBER_ROUTE_NAMES } from '../constants/routes';
import { task } from 'ember-concurrency';
import { showModal } from "sh-www/utils/modal-controls";
import { ADMIN_USER_CONTROLLER } from 'sh-www/controllers/admin/users';

const CLASS_NAME = 'SH-c-users-list';

const MENU_ACTIONS = {
  CHANGE_COMPANY: 'change_company',
  DECLINE_DELETION_REQUEST: 'decline_deletion_request',
  DECLINE_WITH_FEEDBACK: 'decline_with_feedback',
  DECLINE_FOREVER: 'decline_forever',
  DELETE_USER: 'delete_user',
  EDIT_BILLING: 'edit_billing',
  MAKE_ADMIN: 'make_admin',
  MAKE_SUPER_ADMIN: 'make_super_admin',
  MASQUERADE: 'masquerade',
  SEPARATOR: 'separator',
  TOGGLE_APPROVED: 'toggle_approved',
  TOGGLE_SELECT: 'toggle_select',
  VET: 'vet',
};

export const USERS_LIST_COMPONENT = {
  MENU_ACTIONS,
};

export default Component.extend({
  tagName: '',
  CLASS_NAME,

  session: service(),
  router: service(),
  store: service(),
  userCommandService : service('commands/user-commands'),

  currentUser: readOnly('session.currentUser'),

  /** @type {A<User>} */
  users: A([]),

  disableActions: computed(
    'invokeMenu.isRunning',
    '_reloadUser.isRunning',
    'randomizePublicSlug.isRunning',
    'updateUserEmail.isRunning',
    function() {
      return this.invokeMenu.isRunning
        || this._reloadUser.isRunning
        || this.updateUserEmail.isRunning
    }
  ),

  _menuOptionForUser(user, menuAction) {
    switch (menuAction) {
      case USERS_LIST_COMPONENT.MENU_ACTIONS.CHANGE_COMPANY:
        return {
          title: 'Change Company',
          onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.CHANGE_COMPANY);},
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.DELETE_USER:
        return {
          title: 'Delete User',
          onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.DELETE_USER);},
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_DELETION_REQUEST:
        if (user.deletionRequestedAt) {
          return {
            title: 'Decline Deletion Request',
            onClick: () => {
              this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_DELETION_REQUEST);
            },
          };
        }
      case USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_FOREVER:
        if (user.get('isVettable')) {
          return {
            title: 'Decline Forever',
            onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_FOREVER);},
          };
        }
        return null;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_WITH_FEEDBACK:
        if (user.get('isVettable')) {
          return {
            title: 'Decline with Feedback',
            onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_WITH_FEEDBACK);},
          };
        }
        return null;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.EDIT_BILLING:
        return {
          title: 'Edit Billing',
          onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.EDIT_BILLING);},
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_ADMIN:
        if (this.get('currentUser').get('superAdmin')) {
          return {
            title: 'Make Site Admin',
            onClick: () => {
              this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_ADMIN)
            },
          };
        }
      case USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_SUPER_ADMIN:
        if (this.get('currentUser').get('superAdmin')) {
          return {
            title: 'Make Super Admin',
            onClick: () => {
              this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_SUPER_ADMIN);
            },
          };
        }
        return null;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.MASQUERADE:
        return {
          title: 'Masquerade',
          onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.MASQUERADE);},
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR:
        return {
          type: USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED:
        let title;
        if (user.approval === USER.APPROVAL.UN_APPROVED) {
          title = 'Un-ban';
        } else if (user.approval === USER.APPROVAL.UNDEFINED) {
          title = 'Approve';
        } else {
          title = 'Ban';
        }

        return {
          title,
          onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED);},
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_SELECT:
        return {
          title: user.get('select') ? 'Make NOT Select' : 'Make Select',
          onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_SELECT);},
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.VET:
        return {
          title: user.get('isVetted') ? 'Re-Vet' : 'Vet',
          onClick: () => {this.get('invokeMenu').perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.VET);},
        };
      default:
        return null;
    }
  },

  /** @type {A<Ember.Object>} */
  _userMenuOptions: computed(
    'users.@each.{approval,id,select,superAdmin,type}',
    function () {
      const userMenuOptionsMap = {};

      this.get('users').forEach((user) => {
        const userId = user.get('id');
        const menuOptions = A([]);

        let potentialMenuActions;
        switch (user.get('type')) {
          case USER.TYPES.ADMIN:
            potentialMenuActions = [
              USERS_LIST_COMPONENT.MENU_ACTIONS.MASQUERADE,
              USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
              USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED,
              USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
              USERS_LIST_COMPONENT.MENU_ACTIONS.DELETE_USER,
              USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_SUPER_ADMIN,
            ];
            break;
          case USER.TYPES.SELLER:
            potentialMenuActions = [
              USERS_LIST_COMPONENT.MENU_ACTIONS.MASQUERADE,
              USERS_LIST_COMPONENT.MENU_ACTIONS.EDIT_BILLING,
              USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_SELECT,
              USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
              USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED,
              // USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_WITH_FEEDBACK,
              // USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_FOREVER,
              USERS_LIST_COMPONENT.MENU_ACTIONS.VET,
              USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
              USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_ADMIN,
              USERS_LIST_COMPONENT.MENU_ACTIONS.DELETE_USER,
              USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_DELETION_REQUEST,
            ];
            break;
          case USER.TYPES.BUYER:
            potentialMenuActions = [
              USERS_LIST_COMPONENT.MENU_ACTIONS.MASQUERADE,
              USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
              USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED,
              USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
              USERS_LIST_COMPONENT.MENU_ACTIONS.CHANGE_COMPANY,
              USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
              USERS_LIST_COMPONENT.MENU_ACTIONS.DELETE_USER,
              USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_DELETION_REQUEST,
            ];
            break;
          default:
            potentialMenuActions = [
              USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_ADMIN,
              USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
              USERS_LIST_COMPONENT.MENU_ACTIONS.DELETE_USER,
              USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_DELETION_REQUEST,
            ];
        }

        potentialMenuActions.forEach((menuAction) => {
          const menuOption = this._menuOptionForUser(user, menuAction);
          if (menuOption) {
            menuOptions.addObject(menuOption);
          }
        });

        userMenuOptionsMap[userId] = menuOptions;
      });

      return userMenuOptionsMap;
    }
  ),

  /**
   *
   * @param user
   * @return {Promise|*}
   * @private
   */
  _reloadUser: task(function *(user) {
    return this.get('store').findRecord('user', user.get('id'), {
      reload: true,
      include: ADMIN_USER_CONTROLLER.INCLUDES.join(','),
    });
  }),

  invokeMenu: task(function *(user, menuAction) {
    switch (menuAction) {
      case USERS_LIST_COMPONENT.MENU_ACTIONS.CHANGE_COMPANY:
        this.router.transitionTo(EMBER_ROUTE_NAMES.ADMIN__COMPANIES, { queryParams: { changeUser: user.id }});
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_DELETION_REQUEST:
        showModal(this, 'confirmation', {
          isShowCancelButton: true,
          titleText: 'Decline deletion request',
          bodyText: `Are you sure you want to decline ${user.get('firstName')}'s deletion request?`,
          confirmAction: () => {
            this.get('userCommandService').declineDeletion(user).then(() => {
              return this._reloadUser.perform(user);
            });
          },
        });
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_FOREVER:
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.DECLINE_WITH_FEEDBACK:
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.DELETE_USER:
        showModal(this, 'confirmation', {
          isShowCancelButton: true,
          titleText: 'ARE YOU SURE?',
          bodyText: 'This user will be PERMANENTLY DELETED. Do you wish to continue?',
          confirmAction: () => {
            this.get('userCommandService').delete(user).then((result) => {
              return this._reloadUser.perform(user);
            });
          },
        });
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.EDIT_BILLING:
        this.get('router').transitionTo(EMBER_ROUTE_NAMES.ADMIN__USER__BILLING, user.get('slug'));
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_ADMIN:
        showModal(this, 'confirmation', {
          isShowCancelButton: true,
          titleText: 'ARE YOU SURE?',
          bodyText: 'This user will be made a STORYHUNTER ADMINISTRATOR and this action will be reported. Do you wish to continue?',
          confirmAction: () => {
            this.get('userCommandService').setUserType(user, USER.TYPES.ADMIN).then((result) => {
              return this._reloadUser.perform(user);
            })
          },
        });
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.MAKE_SUPER_ADMIN:
        yield this.get('userCommandService').setSuperAdmin(user, true);
        yield this._reloadUser.perform(user);
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.MASQUERADE:
        this.router.transitionTo(EMBER_ROUTE_NAMES.ADMIN__MASQUERADE, user.id);
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED:
        yield this.userCommandService.toggleApproval(user);
        yield this._reloadUser.perform(user);
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_SELECT:
        yield this.get('userCommandService').setSelect(user, !user.get('select'));
        yield this._reloadUser.perform(user);
        break;
      case USERS_LIST_COMPONENT.MENU_ACTIONS.VET:
        window.location.assign(`/admin/user/${user.get('slug')}/rate`);
        break;
    }
  }).enqueue(),

  /**
   * Randomize a user's public slug
   */
  randomizePublicSlug: task(function *(user) {
    yield this.userCommandService.randomizePublicSlug(user);

    yield this._reloadUser.perform(user);
  }),

  /**
   * Update a user's email and reload the user
   */
  updateUserEmail: task(function *(user, email) {
    yield this.userCommandService.updateEmail(user, email);

    yield this._reloadUser.perform(user);
  }).enqueue(),
});
